import { IServiceDetails, IServicePriceData } from 'src/entities/service';
import { handleError } from './../services/api/errorHandler';
import { IPagination } from './../components/Reservations/Paginator';
import { StepType } from './../services/getStepOrder';
import { ICalendar } from './calendar';
import { IResponseDto, isSame, IValidationDto } from './common';
import { ModuleSearchParam } from '../models/modules';
import { IAnswer, IQuestion } from './question';
import { IUserBase, IUser, ISignupUser } from './user';
import { IRequiredFieldInfo } from './company';
import { getDate } from 'src/services/dateTime';
import Moment from 'moment';
import { ISchedule } from './schedule';
import { getScheduleList } from 'src/services/api/schedule';

export const enum ReservationStateType {
    Reserved = 'reserved',
    NotConfirmed = 'notconfirmed',
    Deleted = 'deleted',
}

export const enum ReservationRequestOrder {
    Date,
    Service,
    Calendar,
}

export type IFilterType = 'all' | 'past' | 'future';

export interface IFilter {
    type: IFilterType;
    isAscending: boolean;
}

export interface IReservationDto extends IResponseDto {
    Data: IReservationDetails;
}

export interface IReservationRequest {
    Id: number;
    ClientKey?: string;
}

export interface IReservationQueryOptions {
    pagination?: IPagination;
    filter: IFilter;
    clientKey?: string;
}

export interface IPaidTransactionResponse {
    HasPaidTransaction: boolean;
}

interface IMockReservationDetailsList {
    Reservations: IReservationDetails[];
    TotalCount: number;
    FilteredCount: number;
}

export type IReservationDetailsList = IMockReservationDetailsList;

export interface IReservationDetails extends IReservation {
    Id: number;
    EventState?: ReservationStateType;
    Title: string;
    CalendarName?: string;
    User?: IUser;
    LastModified?: string;
    DurationToUser?: number;
    IsDurationVisible?: boolean;
}

export interface IPendingReservation {
    StartTime: string;
    PersonCount: number;
    CalendarId?: number;
    ServiceId: number;
}

export interface IReservationResponse extends IReservationDetails {
    Blocked: boolean;
    ValidationErrors: IValidationDto[];
    ClientKey: string;
}

/**
 * @property Id        - Unique, generated identifier of the Question
 * @property FieldName - Unique, given identifier of the Question defined by the admin user
 * @property Name      - Visible name of the Question
 * @property Value     - Value of the Question (the answer for the question)
 */
export interface IReservationDate {
    StartTime: Date;
    EndTime?: Date;
    ArriveTime?: Date;
}

/**
 * @property StartTime   - Start time of the reservation (with date and time)
 * @property EndTime     - End time of the reservation (with date and time)
 * @property ArriveTime  - Arrival time planned by the user who made the reservation
 * @property CalendarId  - Id of the selected Calendar
 * @property ServiceId   - Id of the selected Service
 * @property ScheduleId  - Unique identifier of the schedule the reservation belongs to (null if the type of the calendar is not Timetable)
 * @property PersonCount - Number of persons belong to the reservation
 * @property Questions   - Custom questions with answers belong to the reservation
 */
export interface IReservation {
    StartTime: string;
    EndTime: string;
    ArriveTime: string;
    CalendarId?: number;
    Calendar?: ICalendar;
    Service?: IServiceDetails;
    ServiceId: number;
    PersonCount: number;
    Questions?: IQuestion[] | IAnswer[];
    ClientKey?: string;
    ScheduleId?: number;
}

/**
 * Interface for creating reservation request body
 * @property User         - Logged in user data
 * @property Reservations - List of user created reservations
 */
export interface IReservationSaveRequest {
    User: IUserBase | ISignupUser;
    Reservations: IReservation[];
    TermsAccepted: boolean;
}

export const isSignUpUser = (user: IUserBase): user is ISignupUser =>
    (user as any).Password !== undefined;

export interface IReservationModifyRequest {
    Reservation: IReservationDetails;
    User?: IUser;
    ClientKey?: string;
}

/**
 * Type of the @type <T> step value model
 * @property displayValue - Visible representation of the selected value in step
 */
export type StepValue<T extends object> = T & {
    displayValue: string;
};

export interface IDateStepValue {
    startTime: string;
    endTime: string;
    personCount: number;
}
export interface ITimetableStepValue {
    service: IServiceDetails;
    schedule: ISchedule;
    personCount: number;
}

export type ISearchParams = {
    -readonly [key in keyof typeof ModuleSearchParam]: 'unset' | string | null;
};

/**
 * Interface for internal booking flow reservation model
 */
export interface IReservationModel {
    searchParams?: ISearchParams;
    [StepType.Calendar]?: StepValue<ICalendar>;
    [StepType.Service]?: StepValue<IServiceDetails>;
    [StepType.Date]?: StepValue<IDateStepValue>;
    [StepType.Timetable]?: StepValue<ITimetableStepValue>;
    [StepType.CustomerData]?: StepValue<{
        answers: IAnswer[];
        arriveTime: string | null;
    }>;
}

export interface IReservationRequiredFieldProps {
    requiredFieldInfo: IRequiredFieldInfo;
    isAuthenticated?: boolean;
}

export const isEmailRequired = (props: IReservationRequiredFieldProps) => {
    return props.isAuthenticated
        ? props.requiredFieldInfo.IsRegEmailRequired
        : props.requiredFieldInfo.IsEmailRequired;
};

export const isPhoneNumberRequired = (
    props: IReservationRequiredFieldProps
) => {
    return props.isAuthenticated
        ? props.requiredFieldInfo.IsRegPhoneRequired
        : props.requiredFieldInfo.IsPhoneRequired;
};

export const isNameRequired = (props: IReservationRequiredFieldProps) => {
    return props.isAuthenticated
        ? props.requiredFieldInfo.IsRegNameRequired
        : props.requiredFieldInfo.IsNameRequired;
};

export const reservationValueChanged = (
    newData: IReservationModel,
    data: IReservationModel,
    type: 'calendar' | 'service' | 'personCount' | 'date'
) => {
    switch (type) {
        case 'calendar':
            return !isSame(newData[StepType.Calendar], data[StepType.Calendar]);
        case 'service':
            return !isSame(newData[StepType.Service], data[StepType.Service]);

        case 'personCount':
            const newAppointment = newData[StepType.Date];
            const appointment = data[StepType.Date];
            const newPersonCount = newAppointment && newAppointment.personCount;
            const personCount = appointment && appointment.personCount;
            return newPersonCount !== personCount;
        case 'date':
            const newApp = newData[StepType.Date];
            const app = data[StepType.Date];
            const newDate = newApp && newApp.startTime;
            const date = app && app.startTime;
            return (
                (!newDate && !date) ||
                (newDate && date && getDate(date) !== getDate(newDate))
            );
    }
};

export const isWithinModificationPeriod = (
    reservation: IReservationDetails
) => {
    if (!reservation.Calendar) {
        return;
    }

    const start = Moment(reservation.StartTime);
    const now = Moment();

    const minimumTimeUntilStart = Moment.duration(
        reservation.Calendar.MinTimeModify,
        'hours'
    ).asMinutes();
    const timesUntilStart = Moment.duration(start.diff(now)).asMinutes();

    return timesUntilStart >= minimumTimeUntilStart;
};

export const populateReservations = <T extends IReservationDetails>(
    reservations: T[],
    items: ICalendar[] | IServiceDetails[]
): T[] => {
    const isServiceList = (arr: any[]): arr is IServiceDetails[] => {
        return arr[0]?.Price !== undefined;
    };

    return reservations.map((r) =>
        isServiceList(items)
            ? {
                  ...r,
                  Service: items.find((service) => service.Id === r.ServiceId),
              }
            : {
                  ...r,
                  Calendar: items.find(
                      (calendar) => calendar.Id === r.CalendarId
                  ),
              }
    );
};

export const hasSchedule = (reservation: IReservation) =>
    !!reservation.ScheduleId;

export const getSchedule = async (reservation: IReservationDetails) => {
    const { CalendarId, ScheduleId } = reservation;

    if (!CalendarId || !ScheduleId) {
        throw new Error(
            `The reservation of ID ${reservation.Id} has no CalendarId or ScheduleId`
        );
    }

    return getScheduleList(
        CalendarId,
        reservation.StartTime,
        reservation.EndTime,
        true
    )
        .then<ISchedule[][]>(Object.values)
        .then(
            (schedules) =>
                schedules.flat().find((sch) => sch.Id === ScheduleId) ||
                Promise.reject(
                    new Error(
                        `The schedule of ID ${ScheduleId} could not be found`
                    )
                )
        )
        .catch((e) => {
            throw handleError(e);
        });
};

export const reservationModelToServiceSum = (
    model: IReservationModel
): IServicePriceData => {
    const appointment = model[StepType.Date];
    const event = model[StepType.Timetable];

    return appointment
        ? {
              Price: model.service!.Price,
              Currency: model.service!.Currency,
              PaymentMethod: model.service!.PaymentMethod,
              VatPercentage: model.service!.VatPercentage,
              PersonCount: appointment.personCount,
          }
        : {
              Price: event!.service.Price,
              Currency: event!.service.Currency,
              PaymentMethod: event!.service.PaymentMethod,
              VatPercentage: event!.service.VatPercentage,
              PersonCount: event!.personCount,
          };
};

export interface IModifyLocationState {
    Reservation: IReservationDetails;
    ClientKey?: string;
}
